import { ComponentChildren } from 'preact';
import { BtnCopy, Button } from '@components/buttons';
import { IcoRefresh, IcoShow, IcoList, IcoX } from '@components/icons';
import { SaveStatus } from '@components/save-status';
import { SegmentedControl } from '@components/segmented-control';

type PageEditorToolbarProps = {
  publicURL: string;
  viewMode: 'desktop' | 'mobile';
  isDirty: boolean;
  isConnected: boolean;
  header?: ComponentChildren;
  customNavItems?: ComponentChildren;
  showMobileMenu: boolean;
  showCopyButton?: boolean;
  onViewModeChange: (mode: 'desktop' | 'mobile') => void;
  onResetPage?: () => void;
  onToggleMobileMenu: () => void;
  onCloseMobileMenu: () => void;
};

export function PageEditorToolbar({
  publicURL,
  viewMode,
  isDirty,
  isConnected,
  header,
  customNavItems,
  showMobileMenu,
  showCopyButton,
  onViewModeChange,
  onResetPage,
  onToggleMobileMenu,
  onCloseMobileMenu,
}: PageEditorToolbarProps) {
  return (
    <div class="flex justify-between px-4 border-b relative bg-white">
      <div class="mr-4 text-sm flex items-center gap-4">
        {/* Mobile/Desktop Toggle */}
        <SegmentedControl
          value={viewMode}
          options={[
            { value: 'desktop', label: 'Desktop', tooltip: 'Preview desktop layout' },
            { value: 'mobile', label: 'Mobile', tooltip: 'Preview mobile layout' },
          ]}
          onChange={onViewModeChange}
          class="flex-shrink-0"
        />
        <SaveStatus isDirty={isDirty} isConnected={isConnected} />

        {viewMode === 'desktop' && header}
      </div>

      <nav class="hidden lg:flex items-center gap-4 text-sm flex-shrink-0">
        {customNavItems}

        {onResetPage && (
          <Button
            class="inline-flex items-center gap-2 text-inherit hover:bg-gray-200 p-2 px-3 rounded-sm capitalize whitespace-nowrap"
            onClick={onResetPage}
          >
            <IcoRefresh />
            Reset
          </Button>
        )}
        {showCopyButton && <BtnCopy value={publicURL}>Share</BtnCopy>}

        <Button
          class="inline-flex items-center gap-2 text-inherit hover:bg-gray-200 p-2 px-3 rounded-sm"
          href={publicURL}
        >
          <IcoShow />
          View
        </Button>
      </nav>

      {/* Mobile Menu Button */}
      <div class="lg:hidden flex items-center mobile-menu-container flex-shrink-0">
        <Button class="p-2 hover:bg-gray-200 rounded-sm" onClick={onToggleMobileMenu}>
          {showMobileMenu ? <IcoX class="w-5 h-5" /> : <IcoList class="w-5 h-5" />}
        </Button>

        {/* Mobile Menu Dropdown */}
        {showMobileMenu && (
          <div class="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-48">
            {/* Actions */}
            <div class="p-1">
              {customNavItems && (
                <div class="mobile-custom-nav-items" onClick={onCloseMobileMenu}>
                  {customNavItems}
                </div>
              )}

              {onResetPage && (
                <Button
                  class="w-full text-left flex items-center gap-3 p-3 hover:bg-gray-50 rounded text-sm"
                  onClick={() => {
                    onResetPage();
                    onCloseMobileMenu();
                  }}
                >
                  <IcoRefresh class="w-4 h-4" />
                  Reset Page
                </Button>
              )}

              <Button
                class="w-full text-left flex items-center gap-3 p-3 hover:bg-gray-50 rounded text-sm"
                href={publicURL}
                onClick={onCloseMobileMenu}
              >
                <IcoShow class="w-4 h-4" />
                View
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
